/**
 * SedeViewContext — selector "Ver como" para org admins
 *
 * Permite que un org admin elija una sede específica como contexto activo.
 * La sede seleccionada se inyecta automáticamente en operaciones de creación
 * y en el header como indicador visual. Se persiste en sessionStorage.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface ActiveSede {
  id: number;
  name: string;
}

interface SedeViewContextType {
  /** Sede activa seleccionada. null = vista global (todas las sedes) */
  activeSede: ActiveSede | null;
  /** ID de sede activa (null si global) */
  activeSedeId: number | null;
  /** true cuando hay una sede seleccionada (no global) */
  isSedeFiltered: boolean;
  /** Cambiar la sede activa. null = volver a vista global */
  setActiveSede: (sede: ActiveSede | null) => void;
}

const STORAGE_KEY = 'sede_view_active';

const SedeViewContext = createContext<SedeViewContextType | null>(null);

export function SedeViewProvider({ children }: { children: React.ReactNode }) {
  const [activeSede, setActiveSedeState] = useState<ActiveSede | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setActiveSede = useCallback((sede: ActiveSede | null) => {
    setActiveSedeState(sede);
    if (sede) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sede));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo<SedeViewContextType>(() => ({
    activeSede,
    activeSedeId: activeSede?.id ?? null,
    isSedeFiltered: activeSede !== null,
    setActiveSede,
  }), [activeSede, setActiveSede]);

  return (
    <SedeViewContext.Provider value={value}>
      {children}
    </SedeViewContext.Provider>
  );
}

export function useSedeView() {
  const ctx = useContext(SedeViewContext);
  if (!ctx) throw new Error('useSedeView debe usarse dentro de SedeViewProvider');
  return ctx;
}

export default SedeViewContext;
